import React, {
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";
import { ALLOWED_KEYS } from "./const";
import { toast } from "react-toastify";
// Loads the math-field element and its window globals from the bundle, so the
// keyboard does not depend on the MathLive script in public/index.html.
import "mathlive";
import "./styles.css";

/**
 *
 * @prop {string} value - LaTeX content value
 * @prop {function} onChange - Called when input changes with new LaTeX value
 * @prop {function} onBlur - Called when field loses focus with current LaTeX value
 * @prop {function} onClick - Custom click handler (receives click event)
 * @prop {Object} style - Custom styles for the wrapper div
 * @prop {string} className - Additional CSS classes
 * @prop {string} placeholder - Placeholder text for the math field use \text{...}
 *
 * Keyboard Controls:
 * @prop {boolean} readonly - Whether input is disabled
 * @prop {boolean} keyboardEnabled - Whether virtual keyboard is enabled at all
 * @prop {string} keyboardMode - 'auto', 'manual', 'onfocus', or 'off'
 * @prop {string} keyboardLayout - numeric, symbols, alphabetic, greek, minimalist, compact, numeric-only.
 * @prop {boolean} hideOnBlur - Auto-hide keyboard when focus is lost
 * @prop {boolean} blockPhysicalKeyboard - Prevent input from physical keyboard
 * @prop {boolean} limitPhysicalKeyboard - only allow limited physical keyboard functionality
 *
 * Advanced:
 * @prop {Object} options - Direct MathLive options (will override other props if conflicts exist)
 * to apply styles to mathfield use options.style - ex options={{ style: { fontSize: "1.5em" }}}
 */

const MathKeyboard = forwardRef((props, ref) => {
  const {
    value = "",
    onChange,
    onBlur,
    onClick,
    style = {},
    className = "",
    placeholder = "\\text{Enter formula}",

    readonly = false,
    keyboardMode = "manual",
    keyboardLayout = "default",
    hideOnBlur = true,
    blockPhysicalKeyboard = true,
    limitPhysicalKeyboard = true,

    options = {},
  } = props;

  const disabled = readonly || options?.disabled;

  const wrapperRef = useRef(null);
  const mathfieldRef = useRef(null);
  const initializedRef = useRef(false);
  const prevValueRef = useRef(value);

  const handlersRef = useRef({ onChange, onBlur, onClick });
  const optionsRef = useRef(options);

  useEffect(() => {
    handlersRef.current = { onChange, onBlur, onClick };
  }, [onChange, onBlur, onClick]);

  useEffect(() => {
    optionsRef.current = options;
  }, [options]);

  useImperativeHandle(ref, () => ({
    focus: () => mathfieldRef.current?.focus(),
    blur: () => mathfieldRef.current?.blur(),
    getValue: () => mathfieldRef.current?.value || "",
    setValue: (latex) => {
      if (mathfieldRef.current) mathfieldRef.current.value = latex;
    },
    insert: (latex) => mathfieldRef.current?.insert(latex),
    insertText: (text) => mathfieldRef.current?.executeCommand("insert", text),
    select: () => mathfieldRef.current?.select(),
    showKeyboard: () => {
      if (!disabled && window.mathVirtualKeyboard) {
        window.mathVirtualKeyboard.show();
      }
    },
    hideKeyboard: () => {
      if (window.mathVirtualKeyboard) {
        window.mathVirtualKeyboard.hide();
      }
    },
    toggleKeyboard: () => {
      if (!disabled && window.mathVirtualKeyboard) {
        const isVisible = window.mathVirtualKeyboard.visible;
        if (isVisible) {
          window.mathVirtualKeyboard.hide();
        } else {
          window.mathVirtualKeyboard.show();
        }
      }
    },
    isKeyboardVisible: () => window.mathVirtualKeyboard?.visible || false,
    getMathfield: () => mathfieldRef.current,
  }));

  useEffect(() => {
    if (!wrapperRef.current || initializedRef.current) return;

    const mf = wrapperRef.current.querySelector("math-field");
    if (!mf) return;

    mathfieldRef.current = mf;
    initializedRef.current = true;

    // Set initial value
    if (mf.value !== value) {
      mf.value = value;
    }

    const config = {
      readOnly: readonly,
      smartSuperscript: true,
      mathVirtualKeyboardPolicy: keyboardMode,
      virtualKeyboardTargetOrigin: "*",
      mathModeSpace: "\\:",
      placeholder: placeholder || optionsRef.current.placeholder,
      inlineShortcuts: {},
      smartMode: false,
      ...optionsRef.current,
    };

    if (config.style) {
      delete config.style; // Remove style from config to avoid conflicts
    }

    window.MathfieldElement.soundsDirectory = null;
    window.MathfieldElement.keypressVibration = false;

    window.mathVirtualKeyboard.layouts = keyboardLayout;
    window.mathVirtualKeyboard.targetOrigin = "*";

    Object.entries(config).forEach(([k, v]) => {
      mf[k] = v;
    });

    // Handlers using refs
    const handleInput = () => {
      handlersRef.current.onChange?.(mf.value);
    };

    const throttledBlur = throttle(() => {
      handlersRef.current.onBlur?.(mf.value);
      if (hideOnBlur && window.mathVirtualKeyboard) {
        window.mathVirtualKeyboard.hide();
      }
    }, 200);

    const handleFocus = () => {
      if (!disabled && window.mathVirtualKeyboard) {
        window.mathVirtualKeyboard.show();
      }
    };

    const handleClick = (ev) => {
      handlersRef.current.onClick?.(ev);
      if (!disabled) {
        ev.target.focus();
        window.mathVirtualKeyboard?.show();
      }
    };

    const handleKeystroke = (evt) => {
      if (blockPhysicalKeyboard) {
        evt.preventDefault();
        return false;
      }

      if (limitPhysicalKeyboard) {
        const key = evt.key;
        const code = evt.code;

        if (key === "$") {
          evt.preventDefault();
          mathfieldRef.current?.insert("\\$");
          return false;
        }

        if (ALLOWED_KEYS.includes(code) || ALLOWED_KEYS.includes(key)) {
          return true;
        }

        const isAlphanumeric = /^[0-9a-zA-Z]$/.test(key);

        if ((evt.ctrlKey || evt.metaKey) && key.toLowerCase() === "v") {
          evt.preventDefault();
          return false;
        }

        if (!isAlphanumeric) {
          evt.preventDefault();
          toast.warning("Key not allowed! Please use the virtual keyboard", {
            position: "top-right",
            autoClose: 3000,
            hideProgressBar: true,
            closeOnClick: true,
          });
          return false;
        }
      }
    };

    const handlePaste = (evt) => {
      if (limitPhysicalKeyboard) {
        evt.preventDefault();
        return false;
      }
    };

    mf.addEventListener("input", handleInput);
    mf.addEventListener("blur", throttledBlur);
    mf.addEventListener("focusin", handleFocus);
    mf.addEventListener("focusout", throttledBlur);
    mf.addEventListener("click", handleClick);
    mf.addEventListener("paste", handlePaste, { capture: true });

    if (!blockPhysicalKeyboard) {
      mf.addEventListener("keydown", handleKeystroke, { capture: true });
    }
    return () => {
      mf.removeEventListener("keydown", handleKeystroke, { capture: true });
      mf.removeEventListener("paste", handlePaste, { capture: true });
      mf.removeEventListener("input", handleInput);
      mf.removeEventListener("blur", throttledBlur);
      mf.removeEventListener("focusin", handleFocus);
      mf.removeEventListener("focusout", throttledBlur);
      mf.removeEventListener("click", handleClick);
    };
  }, [
    keyboardLayout,
    keyboardMode,
    blockPhysicalKeyboard,
    limitPhysicalKeyboard,
    hideOnBlur,
    disabled,
    readonly,
    placeholder,
  ]);

  useEffect(() => {
    if (
      mathfieldRef.current &&
      prevValueRef.current !== value &&
      mathfieldRef.current.value !== value
    ) {
      mathfieldRef.current.value = value;
      prevValueRef.current = value;
    }
  }, [value]);

  return (
    <div
      ref={wrapperRef}
      className={`mathlive-editor-wrapper ${className}`}
      style={{ width: "100%", ...style }}
    >
      <math-field
        style={{ width: "100%", ...(options?.style ? options?.style : {}) }}
        placeholder={placeholder || options.placeholder}
      />
    </div>
  );
});

MathKeyboard.displayName = "MathKeyboard";

export default MathKeyboard;

function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = Date.now();
    if (now - lastCall >= delay) {
      lastCall = now;
      fn(...args);
    }
  };
}
